import { CollapseProps, Collapse } from "antd"
import { IOvertimeRecord } from "../interface/shiftData"
import { calculateOvertimeDuration, formatDateLong, formatTime } from "../utils"
import upIcon from "assets/svg/chevron-up.svg"
import downIcon from "assets/svg/chevron-down.svg"
import addIcon from "assets/svg/plus.svg"
import { useState } from "react"
import { CollapseRow } from "../components/CollapseRow"

interface ReasonEntry {
  date: string
  overtimeStart: string
  overtimeEnd: string
  id: number | null
}

interface OvertimeCollapseRowProps {
  setReasonEntry: React.Dispatch<React.SetStateAction<ReasonEntry | null>>
  groupBy: "day" | "week" | "month"
  records: IOvertimeRecord[]
  onAddReason: (entry: ReasonEntry) => void
}

export const OvertimeCollapseRow = ({
  setReasonEntry,
  records,
  groupBy,
  onAddReason,
}: OvertimeCollapseRowProps) => {
  const [activeKey, setActiveKey] = useState("")

  const items: CollapseProps["items"] = records
    .sort((a, b) => b.date.localeCompare(a.date))
    .map((item) => {
      const isApproved = item.status?.toLowerCase() === "approved"
      const canAddReason = !item.reason && !isApproved

      return {
        key: item.id?.toString(),
        label: (
          <span className="flex justify-between items-center gap-5">
            <p className="text-[#62748E] text-sm active:text-[#0F172B]">
              {formatDateLong(item.date)}
            </p>
            <button className="flex items-center gap-2">
              {activeKey === item.id.toString() ? (
                <img
                  src={upIcon}
                  width={20}
                  height={20}
                />
              ) : (
                <img
                  src={downIcon}
                  width={20}
                  height={20}
                />
              )}
            </button>
          </span>
        ),
        children: (
          <CollapseRow
            key={item.id}
            rows={[
              {
                label: "Overtime start",
                value: formatTime(item.overtimeStartTime),
              },
              {
                label: "Overtime end",
                value: formatTime(item.overtimeEndTime),
              },
              {
                label: "Ovr. duration",
                value: calculateOvertimeDuration(
                  item.overtimeStartTime,
                  item.overtimeEndTime
                ),
              },
              {
                label: "Reason",
                value: item.reason ? (
                  item.reason
                ) : canAddReason ? (
                  <span
                    className="flex items-center gap-1 cursor-pointer"
                    onClick={() =>
                      onAddReason({
                        date: item.date,
                        overtimeStart: formatTime(item.overtimeStartTime),
                        overtimeEnd: formatTime(item.overtimeEndTime),
                        id: item.id,
                      })
                    }
                  >
                    <img
                      src={addIcon}
                      width={15}
                      height={15}
                    />
                    add reason
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-slate-400 cursor-not-allowed">
                    <img
                      src={addIcon}
                      width={15}
                      height={15}
                      className="opacity-40"
                    />
                    add reason
                  </span>
                ),
              },
              {
                label: "Status",
                value: (
                  <span
                    className={`capitalize ${
                      isApproved
                        ? "text-[#0F172B]"
                        : item.status?.toLowerCase() === "rejected"
                        ? "text-[#E7000B]"
                        : "text-[#DA7705]"
                    }`}
                  >
                    {item.status}
                  </span>
                ),
              },
            ]}
          />
        ),
      }
    })

  return (
    <>
      <style>{`
      .ant-collapse-content {
        border-top: none !important;
      }
      .ant-collapse-content-box {
        padding: 0 !important;
      }
    `}</style>
      <Collapse
        accordion
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key as string)}
        items={items}
        expandIcon={() => null}
        style={{ background: "transparent", border: "none" }}
        className="[&_.ant-collapse-item]:bg-white [&_.ant-collapse-item]:p-0 [&_.ant-collapse-item]:border [&_.ant-collapse-item]:border-[#E2E8F0] [&_.ant-collapse-item]:mb-2 [&_.ant-collapse-item]:rounded-lg [&_.ant-collapse-item]:overflow-hidden [&_.ant-collapse-header]:h-[44px] [&_.ant-collapse-header]:flex [&_.ant-collapse-header]:items-center"
      />
    </>
  )
}
